import { get, pageContext, masks, haveRulesChanged, rulesCache, interpolateString } from 'ctm-util-functions';
import isEqual from 'react-fast-compare';
import * as actions from './actions';

/**
 * Simple debounce function shamelessly stolen and adapted from underscore.js.
 * Waits a configurable number of milliseconds after the last keypress before updating the state with the contents of the input
 *
 * IMPORTANT: Using a function expression and not the prefered arrow function!
 * This is because we want to the callback function "func" to be called with the correct "this".
 * We enforce all callbacks to be arrow functions, so this should not be an issue, but leaving it,
 * so it still works even if someone actually passes "func" as a function declaration.
 *
 * @param {Function} func - A function to be debounced
 * @param {Number} wait - Number of milliseconds to wait
 * @param {Boolean} immediate - write a description
 * @returns {Function} debounce - write a description
 */
const debounce = function (func, wait, immediate = false) {
    let timeout = null;
    return (...args) => {
        const context = this;
        const later = () => {
            timeout = null;
            if (!immediate) {
                func.apply(context, args);
            }
        };
        const callNow = immediate && !timeout;
        clearTimeout(timeout);
        timeout = setTimeout(later, wait);
        if (callNow) func.apply(context, args);
    };
};

export { debounce };

/**
 * Set the visibility state of the field if it has visibility rules
 * @param {String} identifier - the component identifier
 * @param {String} stateKey - The stateKIey for the form that this action is required for
 * @param {Boolean} isVisible - is the component visible
 * @returns {Object} - the visibility action
 */
export const setVisibilityAction = (identifier, stateKey, isVisible = true) => {
    let action = actions.showFieldSet;
    if (!isVisible) {
        action = actions.hideFieldSet;
    }
    return action(identifier, stateKey);
};

/**
 * Determine if the component should be visible or not based on the visibility
 * rule supplied in props.rules. If there is no visibility rule then the component
 * is always visible.
 * @param {Object} props - the set of component props
 * @returns {Boolean} - is the object viable
 */
export const isVisible = (props) => {
    const visRuleResult = get(props, 'rules.visibility.result');
    return visRuleResult !== undefined && visRuleResult !== null ? visRuleResult : true;
};

/**
 * Check to see if any of the selected values (state values used for string templates via selectors) have changed
 * @param {Object} nextProps - The incoming set of props
 * @param {Object} currentProps - The current set of props
 * @returns {Boolean} - True if any of the values have changed
 */
const haveSelectedValuesChanged = (nextProps, currentProps) => {
    if (!currentProps.selectedValues) {
        return false;
    }
    for (const key of Object.keys(currentProps.selectedValues)) {
        if (currentProps.selectedValues[key] !== nextProps.selectedValues[key]) {
            return true;
        }
    }
    return false;
};

/**
 * Check if a value has changed between nextProps and currentProps
 * @param {Object} nextProps - The incoming set of props
 * @param {Object} currentProps - The current set of props
 * @param {String} valueId - The id of the value to check for changes
 * @returns {Boolean} - True if the value for valueId is different between nextProps and currentProps
 */
const hasValueChanged = (nextProps, currentProps, valueId) => {
    if (currentProps[valueId] === undefined) {
        return false;
    }
    return nextProps[valueId] !== currentProps[valueId];
};

/**
 * Check if the toggleRules (if present) have changed.
 * We currently only have one toggle that has rules (visibility) in Energy - elec - tariffType
 * @todo: I'd like a more complete solution for toggle elements as we keep getting new use cases - see EVT-2037
 * @param {Object} nextProps - The incoming set of props
 * @param {Object} currentProps - The current set of props
 * @returns {Boolean} - True if the toggleRules have changed
 */
const haveToggleRulesChanged = (nextProps, currentProps) => {
    if (!nextProps.toggleRules) {
        return false;
    }
    return !isEqual(nextProps.toggleRules, currentProps.toggleRules);
};

/**
 * shouldComponentUpdate function for form containers and elements.
 * Only check the props that we know can change between renders
 *
 * @todo: Extend this to check state or remove state args and only check props
 * @todo: Allow a st of keys to be passed in for elements with extra props - eg options on select
 * @param {Object} nextProps - The incoming set of props
 * @param {Object} nextState - The incoming state
 * @param {Object} currentProps - The current set of props
 * @param {Object} currentState - The current state
 * @returns {Boolean}  - True if the component should update as props have changed
 */
export const shouldComponentUpdate = (nextProps, nextState, currentProps, currentState) => {
    return haveRulesChanged(nextProps, currentProps)
        || hasValueChanged(nextProps, currentProps, 'value')
        || hasValueChanged(nextProps, currentProps, 'touched')
        || hasValueChanged(nextProps, currentProps, 'invalidElements')
        || haveSelectedValuesChanged(nextProps, currentProps)
        || haveToggleRulesChanged(nextProps, currentProps);
};

/**
 * Check for disabled rules on props and return an object that can be applied to disable or enable an element
 *
 * We provide the whole prop in this case - either {disabled: true} or {}
 * We can't just use {disabled: false} as the presence of the disabled attribute on an element will force the element to
 * be disabled, regardless of the value supplied.
 *
 * see https://github.com/facebook/react/issues/961 for a discussion on this
 *
 * @param {Object} props - The component's props
 * @returns {Object} - disabled: true if disabled, otherwise an empty object
 */
export const getDisabledProp = (props) => {
    const disabled = { disabled: true };
    const enabled = {};
    if (!props.rules) {
        return enabled;
    }
    // When a component receives isDisabled by default, rather than having it rely on rules.
    if (props.isDisabled || props.schema.isDisabled) {
        return disabled;
    }

    if (props.elementID) {
        // @todo: temp hack for radios and checkboxes, we should be able to clean this up when we attack those components
        const disabledRule = props.rules[`disabled:${props.elementID}`];
        return disabledRule && disabledRule.result ? disabled : enabled;
    }

    const disabledRule = props.rules.disabled;
    if (disabledRule && disabledRule.result) {
        return disabled;
    }
    return enabled;
};

/**
 * @todo: refactor this to work similarily with getDisabledProp if possible?
 * @param {Object} props - The component's props
 * @returns {Object} - disabled: true if disabled, otherwise an empty object
 */
export const getCheckboxDisabledProp = (props) => {
    const disabled = { disabled: true };
    if (props.schema.isDisabled) {
        return disabled;
    }

    const enabled = {};
    if (!props.rules) {
        return enabled;
    }
    const elementProps = props.optionProps || props.schema.field;
    // Sometimes - eg badge groups, we have to specify an isDisabled rule per badge, so we have to specify the rules 'controls' attr
    // including the value of the badge, eg 'disabled:EG', for single checkboxes, the 'controls' attr is specified as it
    // would be for any other element, eg 'disabled'. These two cases will never co-exist on the same element due to the way
    // checkboxes and rules are defined in the schema.
    const disabledRule = props.rules[`disabled:${elementProps.value}`] || props.rules.disabled;

    return disabledRule && disabledRule.result ? disabled : enabled;
};

/**
 * Determine which classes to apply for our various recording apps e.g. LogRocket
 * @param {Object} schema The field schema
 * @returns {String|Null} The class list to apply to a field.
 */
export const getRecordingMaskClass = (schema) => {
    /* const LogRocketMaskClassName = '_lr-hide';
    return schema.maskInRecording ? LogRocketMaskClassName : null; */

    // leaving func commented, logRocket seems to be the only thing using this and logRocket isn't in use,
    // but its wired through multiple schemas (60+ instances) in each vertical
    // re wiring would be a pain if needed again
    return null;
};
/**
 * If rules.validation doesn't exist on props, it means its optional, as no rules are provided, so return true.
 * If rules.validation.result exists, it'll be true or false, so return that.
 * @param {Object} props The element props from the form schema
 * @returns {Boolean} Whether it is valid or not valid
 */
export const isValid = (props) => {
    if (props.hasOwnProperty('valid')) {
        return props.valid;
    }
    return get(props, 'rules.validation.result', true);
};

/**
 * Determines which suffix to append to an element, to know if its valid or not.
 * @param {Object} props The props from the component.
 * @returns {String} A string used to apply to the component for styles.
 */
export const getComponentStateSuffix = (props) => {
    const isTouched = props.touched;

    const disabled = getDisabledProp(props).disabled === true;
    const isFieldValid = isValid(props);

    const config = { disabled, isFieldValid, isFieldInvalid: !isFieldValid && isTouched };
    return getSuffix(config);
};

/**
 * Determines which suffix to append to a "checked" element, to know if its valid or not.
 * This currently has to be different due to the different requirements of both disabled props
 * and checked/unchecked values.
 * @param {Object} props The props from the component.
 * @returns {String} A string used to apply to the component for styles.
 */
export const getRadioComponentStateSuffix = (props) => {
    const isTouched = props.touched;

    const disabled = getCheckboxDisabledProp(props).disabled === true;
    const elementProps = props.optionProps || props.schema.field;
    const isFieldValid = props.value === interpolateString(elementProps.value)(props.selectedValues);
    const isFieldInvalid = !isValid(props);

    const config = { disabled, isFieldValid, isFieldInvalid: isFieldInvalid && isTouched };
    return getSuffix(config);
};

/**
 * Helper method
 * @param {Object} config The three configuration options for determing state
 * @returns {String} The state string suffix
 */
const getSuffix = (config) => {
    let suffix = '';
    const { disabled, isFieldValid, isFieldInvalid } = config;
    if (disabled) {
        suffix = `${suffix}_disabled`;
    } if (isFieldValid) {
        suffix = `${suffix}_valid`;
    } else if (isFieldInvalid) {
        suffix = `${suffix}_invalid`;
    }
    return suffix;
};

/**
 * Extract the ranges for a given element rule and apply any specified masks before returning
 * @param {Object} rule - The validation rule to get the range values for
 * @param {String} maskName - The name of the mask to apply
 * @returns {Object} - The processed min / max rule range values
 */
export const getRuleRangeValues = (rule, maskName = null) => {
    const elementRule = rulesCache.getElementRuleForId(rule.id);
    const rulesModel = pageContext.getProps().models.rulesModel;

    const stateValues = rulesCache.getStateValuesForId(rule.id);

    const rules = rulesModel.extractRules(elementRule.rule);
    if (elementRule.selectedRules) {
        try {
            rules.push(rulesModel.getSelectedRule(elementRule.selectedRules, stateValues, elementRule));
        } catch (e) {
            // only push a passing selectedRule, otherwise, do nothing
        }
    }

    const rangeRules = rules.filter((_rule) => _rule.schema.filterOption === 'range');
    let range = {};
    if (rangeRules.length) {
        // @todo: currently only uses range from the first range rule it finds - otherwise we'll need to specify which rule ID to use in contentProps
        // need to deep clone range as we're converting to strings etc
        range = JSON.parse(JSON.stringify(rangeRules[0].schema.filterValue));
        if (maskName) {
            for (const key of Object.keys(range)) {
                range[key] = masks[maskName](String(range[key]), ',');
            }
        }
    }
    return range;
};
